"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { LuX } from "react-icons/lu";
import { cn, isToastElement } from "../../lib/utils";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { SHEET_STYLES } from "./constants";
import { SheetContentProps } from "./types";

const Sheet = DialogPrimitive.Root;

const SheetTrigger = DialogPrimitive.Trigger;

const SheetPortal = DialogPrimitive.Portal;

const SheetClose = DialogPrimitive.Close;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(SHEET_STYLES.overlay, className)}
    {...props}
  />
));
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;

const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      side = "right",
      size = "xl",
      className,
      overlayClassName,
      children,
      noDescription,
      closeIcon,
      headerTitle,
      onPointerDownOutside,
      ...props
    },
    ref
  ) => {
    const isMobile = useMediaQuery("(max-width: 768px)");

    const handlePointerDownOutside: NonNullable<SheetContentProps["onPointerDownOutside"]> = (event) => {
      if (isToastElement(event.target)) {
        event.preventDefault();
        return;
      }
      onPointerDownOutside?.(event);
    };

    const getSideStyles = () => {
      if (isMobile) {
        switch (side) {
          case "left":
            return SHEET_STYLES.mobileLeft;
          case "right":
            return SHEET_STYLES.mobileRight;
          case "bottom":
            return SHEET_STYLES.mobileBottom;
          case "top":
          default:
            return SHEET_STYLES.mobileFullScreen;
        }
      }

      switch (side) {
        case "top":
          return SHEET_STYLES.contentTop;
        case "bottom":
          return SHEET_STYLES.contentBottom;
        case "left":
          return SHEET_STYLES.contentLeft;
        case "right":
        default:
          return SHEET_STYLES.contentRight;
      }
    };

    const getSizeStyles = () => {
      if (isMobile) {
        return "";
      }

      switch (size) {
        case "sm":
          return SHEET_STYLES.sizesSm;
        case "md":
          return SHEET_STYLES.sizesMd;
        case "lg":
          return SHEET_STYLES.sizesLg;
        case "xl":
          return SHEET_STYLES.sizesXl;
        case "full":
          return SHEET_STYLES.sizesFull;
        default:
          return SHEET_STYLES.sizesXl;
      }
    };

    return (
      <SheetPortal>
        <SheetOverlay className={overlayClassName} />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            SHEET_STYLES.content,
            getSideStyles(),
            getSizeStyles(),
            className
          )}
          aria-describedby={
            noDescription ? undefined : props["aria-describedby"]
          }
          onPointerDownOutside={handlePointerDownOutside}
          {...props}
        >
          {headerTitle && (
            <div className="flex items-center justify-between mb-4">
              <DialogPrimitive.Title className="text-[24px] font-bold text-ticketon-lighter-blue pr-12">
                {headerTitle}
              </DialogPrimitive.Title>
              {closeIcon !== null && (
                <DialogPrimitive.Close className={SHEET_STYLES.closeButton}>
                  {closeIcon || <LuX className="h-4 w-4" />}
                  <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
              )}
            </div>
          )}
          {!headerTitle && closeIcon !== null && (
            <DialogPrimitive.Close className={SHEET_STYLES.closeButton}>
              {closeIcon || <LuX className="h-4 w-4" />}
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
          {children}
        </DialogPrimitive.Content>
      </SheetPortal>
    );
  }
);
SheetContent.displayName = DialogPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn(SHEET_STYLES.header, className)} {...props} />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn(SHEET_STYLES.footer, className)} {...props} />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(SHEET_STYLES.title, className)}
    {...props}
  />
));
SheetTitle.displayName = DialogPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(SHEET_STYLES.description, className)}
    {...props}
  />
));
SheetDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
